import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree} from '@angular/router';
import {Observable, of} from 'rxjs';
import {switchMap, take, tap} from 'rxjs/operators';
import {AuthService} from './auth.service';

/**
 * Guard obsługujący proces autoryzacji dla modułu Rezerwacji (Reservations).
 * Wdraża mechanizm "Return URL" (Deep Linking). Zapewnia płynne doświadczenie użytkownika
 * w przypadku wejścia w link do rezerwacji bez aktywnej sesji, przekazując niezbędne
 * parametry (UUID) do panelu logowania, aby po pomyślnej autoryzacji wznowić przerwany proces.
 */
@Injectable({
  providedIn: 'root'
})
export class ReservationAuthGuard implements CanActivate {
  constructor(
    private authService: AuthService,
    private router: Router,
  ) {
  }

  /**
   * Weryfikuje stan uwierzytelnienia przed aktywacją widoku rezerwacji.
   * Jeśli wykryje brak sesji, przechwytuje identyfikator rezerwacji ('uuid') z parametrów URL
   * i deleguje go do usługi autoryzacyjnej w próbie automatycznego odzyskania sesji
   * (autoLoginWithReturnUrl) lub podczas przekierowania na widok logowania ('signin').
   *
   * @param route Migawka aktywowanej ścieżki, z której pobierane są parametry queryParams.
   * @param state Migawka stanu routera.
   * @returns Observable emitujący wartość logiczną lub bezpieczne drzewo routingu (UrlTree).
   */
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.authService.userIsAuthenticated.pipe(
      take(1),
      switchMap(isAuthenticated => {
        if (!isAuthenticated) {
          let uuid = route.queryParams['uuid'];
          return this.authService.autoLoginWithReturnUrl({queryParams: {rut: 'vet-reservation', ruuid: uuid}});
        } else {
          return of(isAuthenticated);
        }
      }),
      tap(isAuthenticated => {
        if (!isAuthenticated) {
          let uuid = route.queryParams['uuid'];
          this.router.navigate(['signin'], {queryParams: {rut: 'vet-reservation', ruuid: uuid}});
        }
      })
    );
  }

}
